import React from "react";
import { Alert, Dialog, DialogContent, DialogActions, Button, Divider, IconButton, TextField, InputAdornment } from "@mui/material";
import { Close } from "@mui/icons-material";
import AppSelectField from "../../../lib/components/AppSelectField";
import { useQuery } from "@tanstack/react-query";
import walletService from "../../../service/walletService";
import { moneyFormat } from "../../../utils/moneyFormat";
import { toast } from "react-toastify";
import BTNLoader from "../../../lib/components/BTNLoader";
import { useNavigate } from "react-router-dom";
import pathnames from "../../../constants/pathnames";
import NumberFormatCustom from "../../../lib/components/CurrencyFormat";

const InitiatePayoutModal = ({ open, onClose, referrer, userId, refetch }) => {
  const navigate = useNavigate();
  const [amount, setAmount] = React.useState("");
  const [adminPin, setAdminPin] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorText, setErrorText] = React.useState("");
  const [selectedBankId, setSelectedBankId] = React.useState("");

  const resolvedUserId = userId || referrer?.accountMeta?._id || referrer?.accountMeta?.id || (typeof referrer?.account === "string" ? referrer.account : referrer?.account?._id || referrer?.account?.id) || referrer?.accountId || referrer?.userId;

  // Use useQuery to leverage cache and avoid 0 available balance / loading lag
  const walletQuery = useQuery({
    queryKey: ["wallet", resolvedUserId],
    queryFn: () => walletService.getWallet(resolvedUserId),
    enabled: open && Boolean(resolvedUserId),
  });

  const banksQuery = useQuery({
    queryKey: ["banks", resolvedUserId],
    queryFn: () => walletService.getBanks(resolvedUserId),
    enabled: open && Boolean(resolvedUserId),
  });

  const wallet = walletQuery.data?.data?.data;
  // Memoised so bankOptions stays stable; otherwise the pre-select effect below resets the admin's choice every render
  const banks = React.useMemo(() => banksQuery.data?.data?.data || [], [banksQuery.data]);

  React.useEffect(() => {
    if (open) {
      setAmount("");
      setAdminPin("");
      setErrorText("");
    }
  }, [open]);

  const bankOptions = React.useMemo(() => {
    return banks.map((b) => ({
      id: b.id || b._id,
      name: b.name || b.bankName,
      accountNumber: b.accountNumber,
      accountName: b.accountName,
      isPrimary: b.isPrimary,
    }));
  }, [banks]);

  // Pre-select default bank when bankOptions change
  React.useEffect(() => {
    if (bankOptions.length > 0) {
      const primary = bankOptions.find((b) => b.isPrimary) || bankOptions[0];
      setSelectedBankId(primary.id);
    } else {
      setSelectedBankId("");
    }
  }, [bankOptions]);

  const availableBalance = wallet
    ? wallet.balance
    : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorText("");

    const numAmount = Number(amount);
    if (!amount || numAmount <= 0) {
      setErrorText("Please enter a valid payout amount");
      return;
    }

    if (numAmount > availableBalance) {
      setErrorText("Insufficient available balance");
      return;
    }

    if (adminPin.length !== 4) {
      setErrorText("Please enter your 4-digit security PIN");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        userId: resolvedUserId,
        amount: numAmount,
        adminPin,
      };
      if (selectedBankId) {
        payload.bankAccountId = selectedBankId;
      }
      await walletService.payout(payload);
      toast.success("Payout initiated successfully!");
      if (refetch) refetch();
      // Also invalidates React Query cache so balance is updated globally
      walletQuery.refetch();
      onClose();
      const targetReferrerId = referrer?.referrerId || referrer?._id || referrer?.id;
      if (targetReferrerId) {
        navigate(`${pathnames.PAYOUTS}/${targetReferrerId}`);
      }
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || "Payout initiation failed";
      setErrorText(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <div className="d-flex justify-content-between gap-4 p-3 py-2 pe-2">
        <div>
          <div className="fs-5 fw-medium">Process Payout</div>
          <div className="fs-sm text-secondary">
            Enter your required withdrawal amount. Payouts are automatically routed to the referrer's primary bank account.
          </div>
        </div>
        <div>
          <IconButton onClick={onClose} disabled={isSubmitting}>
            <Close />
          </IconButton>
        </div>
      </div>
      <Divider className="bg-secondary-subtle" />
      <DialogContent>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="d-block">
              Amount to withdraw <span className="text-danger">*</span>
            </label>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter amount"
              name="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={isSubmitting}
              InputProps={{
                inputComponent: NumberFormatCustom,
                startAdornment: (
                  <InputAdornment position="start">
                    <span>₦</span>
                  </InputAdornment>
                ),
              }}
              helperText={
                !walletQuery.isSuccess
                  ? "Loading wallet balance..."
                  : `Available Balance: ${moneyFormat(availableBalance, "NGN")}`
              }
              className="bg-white"
            />
          </div>

          <div className="mb-3">
            <label className="d-block">Destination account</label>
            {banksQuery.isSuccess && bankOptions.length === 0 ? (
              <Alert severity="error">No bank details on file — payout will fail</Alert>
            ) : (
              <AppSelectField
                value={banksQuery.isSuccess ? selectedBankId : ""}
                placeholder={banksQuery.isSuccess ? undefined : "Loading destination account details..."}
                isLoading={!banksQuery.isSuccess}
                onChange={(e) => setSelectedBankId(e.target.value)}
                disabled={isSubmitting || !banksQuery.isSuccess}
                items={bankOptions.map((bank) => ({
                  value: bank.id,
                  label: `${bank.name} — ${bank.accountNumber}${bank.accountName ? ` (${bank.accountName})` : ""}${bank.isPrimary ? " [Primary]" : ""}`,
                }))}
              />
            )}
          </div>

          <div className="mb-4">
            <label className="d-block text-center mb-2">
              Verify Admin PIN <span className="text-danger">*</span>
            </label>
            <PinInput value={adminPin} onChange={setAdminPin} disabled={isSubmitting} />
          </div>

          {errorText && (
            <div className="text-danger text-center fs-sm mb-3">{errorText}</div>
          )}

          <DialogActions className="px-0">
            <Button onClick={onClose} disabled={isSubmitting} type="button">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !amount || adminPin.length !== 4}
              variant="contained"
              color="secondary"
              startIcon={<BTNLoader isLoading={isSubmitting} />}
            >
              Initiate Payout
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PinInput = ({ value, onChange, length = 4, disabled, prefix = "admin-payout-pin" }) => {
  const handleInputChange = (e, idx) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    if (!val) return;

    const newVal = value.split("");
    newVal[idx] = val[val.length - 1];
    const result = newVal.join("").slice(0, length);
    onChange(result);

    if (idx < length - 1) {
      const nextInput = document.getElementById(`${prefix}-box-${idx + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      const newVal = value.split("");
      newVal[idx] = "";
      const result = newVal.join("");
      onChange(result);

      if (idx > 0) {
        const prevInput = document.getElementById(`${prefix}-box-${idx - 1}`);
        prevInput?.focus();
      }
    }
  };

  return (
    <div className="d-flex gap-2 justify-content-center">
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          id={`${prefix}-box-${idx}`}
          type="password"
          maxLength={1}
          disabled={disabled}
          value={value[idx] || ""}
          onChange={(e) => handleInputChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          className="form-control text-center fw-bold fs-4 bg-light focus-border-primary"
          style={{ width: "50px", height: "50px" }}
        />
      ))}
    </div>
  );
};

export default InitiatePayoutModal;
